import {
  Alert, Button, DatePicker, Input, InputNumber, Select, Space, Switch, Tag,
  Tooltip, Typography,
} from "antd";
import dayjs from "dayjs";
import { DeleteOutlined, PlusOutlined, SwapOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FieldRef } from "./types";

const { Text } = Typography;

/** One leaf of the rule language: "<field> <op> <value>". */
type Clause = { field: string; op: string; value?: unknown };

type Props = {
  /** The stored rule. {} means the stage always applies. */
  value: Record<string, any>;
  onChange: (rule: Record<string, any>) => void;
  /** Fields collected from the template's sections, so the admin picks rather
   *  than types — a typo here is a rule that silently never matches. */
  fields: FieldRef[];
  /** Position of the stage being edited, so a rule about an answer that comes
   *  later can be called out where it is written rather than at save time. */
  stageIndex: number;
  ops: string[];
  /** Which of `ops` compare magnitudes. Sent by the server rather than listed
   *  here so the builder and `conditions.ORDER_OPS` cannot drift apart. */
  orderOps: string[];
  disabled?: boolean;
};

// Operators that stand alone: "is answered" / "is blank" take no value.
const NO_VALUE = ["filled", "empty"];

// Operators the backend list-matches. These need a multi-value editor: "is one
// of A, B, C" was impossible while the value was a single text box, even though
// the rule language supported it all along.
const MULTI_VALUE = ["in", "not_in", "contains", "not_contains"];

// Ordered comparison wording, by what is being compared. "is after" and "is
// more than" are the same operator; which one an admin should see depends on
// the field, and picking the wrong word is how a date rule gets read backwards.
// Mirrors `_ORDER_WORDS` in main/conditions.py.
const NUMBER_OP_LABELS: Record<string, string> = {
  gt: "is more than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  between: "is between",
};

const DATE_OP_LABELS: Record<string, string> = {
  gt: "is after",
  gte: "is on or after",
  lt: "is before",
  lte: "is on or before",
  between: "is between",
};

const OP_LABELS: Record<string, string> = {
  eq: "is",
  ne: "is not",
  in: "is one of",
  not_in: "is none of",
  contains: "includes",
  not_contains: "does not include",
  filled: "is answered",
  empty: "is blank",
  ...NUMBER_OP_LABELS,
};

// Components whose answers are dates. A threshold on one of these should offer
// a date picker and read "is after", not a number box reading "is more than".
const DATE_COMPONENTS = ["DatePicker", "DateRange", "TimePicker"];

const isDateField = (field?: FieldRef) =>
  !!field &&
  (DATE_COMPONENTS.some((c) => (field.component || "").includes(c)) ||
    /date|_at$/i.test(field.name));

/** A field reference used as a value: `{"field": "start_date"}`. */
const isFieldRef = (v: unknown): v is { field: string } =>
  !!v && typeof v === "object" && typeof (v as any).field === "string";

const isClause = (node: unknown): node is Clause =>
  !!node && typeof node === "object" && typeof (node as Clause).field === "string";

/** Read a stored value into the shape the editor for this operator wants. */
const toEditable = (op: string, value: unknown): string[] | string => {
  if (MULTI_VALUE.includes(op)) {
    if (Array.isArray(value)) return value.map(String);
    return value === undefined || value === null || value === "" ? [] : [String(value)];
  }
  if (Array.isArray(value)) return value.length ? String(value[0]) : "";
  return value === undefined || value === null ? "" : String(value);
};

/** A `between` value is always a two-item [low, high] pair. */
const toPair = (value: unknown): unknown[] => {
  const list = Array.isArray(value) ? value : value === undefined ? [] : [value];
  return [list[0] ?? "", list[1] ?? ""];
};

/** A threshold compares one value, not a set. A field reference survives. */
const toScalar = (value: unknown): unknown => {
  if (isFieldRef(value)) return value;
  const flat = Array.isArray(value) ? value[0] : value;
  return flat === undefined || flat === null ? "" : flat;
};

/**
 * Editor for a stage's "applies when" rule.
 *
 * The stored form is the JSON rule language the backend evaluates. This exposes
 * the shape admins actually need — a list of clauses joined by all/any,
 * optionally negated — rather than the full grammar. A rule nested deeper than
 * that (a group inside a group) is shown read-only rather than flattened,
 * because rewriting someone's rule to fit the editor would change what the
 * stage does.
 */
const ConditionBuilder = ({
  value,
  onChange,
  fields,
  stageIndex,
  ops,
  orderOps,
  disabled,
}: Props) => {
  const { t } = useTranslation();

  // A clause with no field yet has no shape in the rule language — `emit` drops
  // it — so a freshly added row lives here until its field is picked. Without
  // this, "Add a condition" emitted the rule unchanged and the button was dead.
  const [drafts, setDrafts] = useState<Clause[]>([]);

  const byName = new Map(fields.map((f) => [f.name, f]));

  // Grouped by the section that asks the field: two sections can both have a
  // field called `title`, and a flat list makes them indistinguishable.
  const fieldGroups = Array.from(
    fields.reduce((acc, field) => {
      const list = acc.get(field.sectionTitle) ?? [];
      list.push(field);
      acc.set(field.sectionTitle, list);
      return acc;
    }, new Map<string, FieldRef[]>())
  ).map(([sectionTitle, list]) => ({
    label: t(sectionTitle),
    options: list.map((field) => ({
      value: field.name,
      // The stored name is shown beside the label because the label is what an
      // admin recognises and the name is what actually goes into the rule.
      label: `${field.label}  ·  ${field.name}`,
    })),
  }));

  type Parsed = { join: "all" | "any"; clauses: Clause[]; negated: boolean };

  const parseBody = (node: any): Omit<Parsed, "negated"> | null => {
    if (!node || Object.keys(node).length === 0) return { join: "all", clauses: [] };
    if (isClause(node)) return { join: "all", clauses: [node] };
    for (const join of ["all", "any"] as const) {
      const list = node[join];
      if (Array.isArray(list) && list.every(isClause))
        return { join, clauses: list as Clause[] };
    }
    return null;
  };

  const parse = (): Parsed | null => {
    if (value && typeof value === "object" && "not" in value) {
      const inner = parseBody((value as any).not);
      return inner ? { ...inner, negated: true } : null;
    }
    const body = parseBody(value);
    return body ? { ...body, negated: false } : null;
  };

  const parsed = parse();

  if (!parsed) {
    return (
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <Text type="secondary">
          {t("This rule is too complex to edit here. It still applies.")}
        </Text>
        <Text code style={{ fontSize: 12 }}>
          {JSON.stringify(value)}
        </Text>
        <Button size="small" danger disabled={disabled} onClick={() => onChange({})}>
          {t("Clear the rule")}
        </Button>
      </Space>
    );
  }

  const { join, clauses, negated } = parsed;

  const emit = (
    nextJoin: "all" | "any",
    next: Clause[],
    nextNegated = negated
  ) => {
    const cleaned = next.filter((c) => c.field);
    if (cleaned.length === 0) return onChange({});
    const body =
      cleaned.length === 1 ? { ...cleaned[0] } : { [nextJoin]: cleaned };
    onChange(nextNegated ? { not: body } : body);
  };

  /** Apply a patch to one clause, reshaping `value` when the operator changes. */
  const patched = (clause: Clause, patch: Partial<Clause>): Clause => {
    const next = { ...clause, ...patch };
    if (patch.op) {
      // The stored shape follows the operator: a list for the list-matching
      // ones, a pair for `between`, a plain scalar otherwise. Carry the old
      // value across rather than dropping what was typed.
      if (NO_VALUE.includes(patch.op)) delete next.value;
      else if (patch.op === "between") next.value = toPair(clause.value);
      else if (orderOps.includes(patch.op)) next.value = toScalar(clause.value);
      else if (orderOps.includes(clause.op))
        // Leaving a threshold: a number is not an option label, so anything
        // carried over would be a rule that never matches. Start clean.
        next.value = MULTI_VALUE.includes(patch.op) ? [] : "";
      else next.value = toEditable(patch.op, clause.value);
    }
    return next;
  };

  // What is on screen: the stored clauses, then any half-written row. Indices
  // below `clauses.length` address the rule, the rest address `drafts`.
  const rows = clauses.concat(drafts);

  const update = (index: number, patch: Partial<Clause>) => {
    if (index < clauses.length)
      return emit(
        join,
        clauses.map((c, i) => (i === index ? patched(c, patch) : c))
      );

    const at = index - clauses.length;
    const next = patched(drafts[at], patch);
    // Picking the field is what makes the row storable; until then editing it
    // must not reach `onChange`, or every keystroke would emit `{}`.
    if (!next.field)
      return setDrafts(drafts.map((c, i) => (i === at ? next : c)));
    setDrafts(drafts.filter((_, i) => i !== at));
    emit(join, [...clauses, next]);
  };

  const remove = (index: number) => {
    if (index < clauses.length)
      return emit(
        join,
        clauses.filter((_, i) => i !== index)
      );
    const at = index - clauses.length;
    setDrafts(drafts.filter((_, i) => i !== at));
  };

  const add = () =>
    setDrafts([...drafts, { field: "", op: "contains", value: [] }]);

  if (rows.length === 0) {
    return (
      <Space>
        <Tag>{t("Always applies")}</Tag>
        <Button
          size="small"
          icon={<PlusOutlined />}
          disabled={disabled}
          onClick={add}
        >
          {t("Add a condition")}
        </Button>
      </Space>
    );
  }

  /**
   * The value side of a threshold.
   *
   * A number box or a date picker, not the option multi-select the set
   * operators get: `gt` against "Plant A" is a rule that saves, publishes and
   * can never be true, and the server can only report that after the fact.
   *
   * The ⇄ button switches the value to *another field* — `{"field": "..."}`,
   * the one indirection the rule language has. It is what lets a rule say "the
   * end date is after the start date" without arithmetic, and there is no way
   * to type that into a value box.
   */
  const orderedEditor = (clause: Clause, index: number) => {
    const field = byName.get(clause.field);
    const asDate = isDateField(field);

    const one = (
      value: unknown,
      onSet: (v: unknown) => void,
      placeholder: string,
      width: number
    ) =>
      asDate ? (
        <DatePicker
          size="small"
          style={{ width }}
          placeholder={placeholder}
          disabled={disabled}
          value={
            value && dayjs(String(value)).isValid()
              ? dayjs(String(value))
              : null
          }
          onChange={(d) => onSet(d ? d.format("YYYY-MM-DD") : "")}
        />
      ) : (
        <InputNumber
          size="small"
          style={{ width }}
          placeholder={placeholder}
          disabled={disabled}
          value={value === "" || value === undefined ? null : Number(value)}
          onChange={(v) => onSet(v === null || v === undefined ? "" : v)}
          // People type thresholds with separators. The server parses "1 200"
          // and "12,5" tolerantly; the box should not fight them for it.
          parser={(text) => (text ?? "").replace(/[\s ]/g, "").replace(",", ".") as any}
        />
      );

    if (clause.op === "between") {
      const [low, high] = toPair(clause.value);
      return (
        <Space size={4}>
          {one(low, (v) => update(index, { value: [v, high] }), t("From"), 120)}
          <Text type="secondary">{t("and")}</Text>
          {one(high, (v) => update(index, { value: [low, v] }), t("To"), 120)}
        </Space>
      );
    }

    const ref = isFieldRef(clause.value);
    return (
      <Space size={4}>
        {ref ? (
          <Select
            size="small"
            showSearch
            optionFilterProp="label"
            style={{ width: 210 }}
            placeholder={t("Field to compare with")}
            value={(clause.value as { field: string }).field || undefined}
            disabled={disabled}
            onChange={(v) => update(index, { value: { field: v } })}
            options={fieldGroups}
          />
        ) : (
          one(
            toScalar(clause.value),
            (v) => update(index, { value: v }),
            asDate ? t("Date") : t("Amount"),
            170
          )
        )}
        <Tooltip
          title={
            ref
              ? t("Compare with a fixed value instead")
              : t("Compare with another field instead")
          }
        >
          <Button
            size="small"
            type={ref ? "primary" : "default"}
            icon={<SwapOutlined />}
            disabled={disabled}
            aria-label={t("Compare with another field instead")}
            onClick={() =>
              update(index, { value: ref ? "" : { field: "" } })
            }
          />
        </Tooltip>
      </Space>
    );
  };

  const valueEditor = (clause: Clause, index: number) => {
    if (NO_VALUE.includes(clause.op)) return null;
    if (orderOps.includes(clause.op)) return orderedEditor(clause, index);

    // When the field offers a fixed set of answers, offer exactly those. Typing
    // "Plant A" where the schema stores "planta" produces a rule that is
    // perfectly valid, saves, publishes — and never matches.
    const options = byName.get(clause.field)?.options ?? [];
    const multi = MULTI_VALUE.includes(clause.op);

    if (options.length > 0) {
      const choices = options.map((o) => ({
        value: o.value,
        label: o.label === o.value ? o.label : `${o.label}  ·  ${o.value}`,
      }));
      return multi ? (
        <Select
          size="small"
          mode="multiple"
          allowClear
          style={{ minWidth: 240 }}
          placeholder={t("Pick the answers")}
          value={toEditable(clause.op, clause.value) as string[]}
          disabled={disabled}
          onChange={(v) => update(index, { value: v })}
          options={choices}
        />
      ) : (
        <Select
          size="small"
          allowClear
          style={{ minWidth: 210 }}
          placeholder={t("Pick the answer")}
          value={(toEditable(clause.op, clause.value) as string) || undefined}
          disabled={disabled}
          onChange={(v) => update(index, { value: v ?? "" })}
          options={choices}
        />
      );
    }

    if (multi) {
      return (
        <Select
          size="small"
          mode="tags"
          style={{ minWidth: 220 }}
          placeholder={t("Answers (press Enter after each)")}
          value={toEditable(clause.op, clause.value) as string[]}
          disabled={disabled}
          onChange={(v) => update(index, { value: v })}
          tokenSeparators={[","]}
        />
      );
    }
    return (
      <Input
        size="small"
        style={{ width: 190 }}
        placeholder={t("Answer")}
        value={toEditable(clause.op, clause.value) as string}
        disabled={disabled}
        onChange={(e) => update(index, { value: e.target.value })}
      />
    );
  };

  /** A rule about an answer this stage has not collected yet can never match. */
  const tooLate = (clause: Clause) => {
    const field = byName.get(clause.field);
    return !!field && stageIndex >= 0 && field.stageIndex >= stageIndex;
  };
  const unknown = (clause: Clause) => !!clause.field && !byName.has(clause.field);

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Space wrap size={6}>
        {clauses.length > 1 && (
          <>
            <Text type="secondary">{t("This stage applies when")}</Text>
            <Select
              size="small"
              value={join}
              disabled={disabled}
              style={{ width: 150 }}
              onChange={(v) => emit(v, clauses)}
              options={[
                { value: "all", label: t("all of these hold") },
                { value: "any", label: t("any of these hold") },
              ]}
            />
          </>
        )}
        <Tooltip
          title={t("Run this stage when the conditions below are NOT met.")}
        >
          <Space size={6}>
            <Switch
              size="small"
              checked={negated}
              disabled={disabled}
              onChange={(v) => emit(join, clauses, v)}
            />
            <Text type="secondary">{t("Invert")}</Text>
          </Space>
        </Tooltip>
      </Space>

      {rows.map((clause, index) => (
        <div key={index} style={{ width: "100%" }}>
          <Space wrap size={6} style={{ width: "100%" }}>
            <Select
              size="small"
              showSearch
              optionFilterProp="label"
              placeholder={t("Field")}
              style={{ width: 250 }}
              value={clause.field || undefined}
              disabled={disabled}
              status={tooLate(clause) || unknown(clause) ? "error" : undefined}
              onChange={(v) => update(index, { field: v, value: undefined })}
              options={fieldGroups}
            />
            <Select
              size="small"
              style={{ width: 175 }}
              value={clause.op}
              disabled={disabled}
              onChange={(v) => update(index, { op: v })}
              options={ops.map((op) => ({
                value: op,
                // A date field says "is after"; a number says "is more than".
                // Same operator either way — see NUMBER/DATE_OP_LABELS.
                label: t(
                  (isDateField(byName.get(clause.field))
                    ? DATE_OP_LABELS[op]
                    : undefined) ??
                    OP_LABELS[op] ??
                    op
                ),
              }))}
            />
            {valueEditor(clause, index)}
            <Button
              size="small"
              type="text"
              danger
              disabled={disabled}
              icon={<DeleteOutlined />}
              onClick={() => remove(index)}
              aria-label={t("Remove this condition")}
            />
          </Space>

          {unknown(clause) && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 6 }}
              message={t("No section asks “{{field}}”, so this rule never matches.", {
                field: clause.field,
              })}
            />
          )}
          {!unknown(clause) && tooLate(clause) && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 6 }}
              message={t(
                "This answer is not collected until this stage or later, so the rule can never be true when it is checked."
              )}
            />
          )}
        </div>
      ))}

      <Button
        size="small"
        icon={<PlusOutlined />}
        disabled={disabled}
        onClick={add}
      >
        {t("Add a condition")}
      </Button>
    </Space>
  );
};

export default ConditionBuilder;
